/**
 * Palettes are independent of visualizations: a gradient baked to a 256×1 RGBA texture that
 * shaders sample with palette(t). Gradients interpolate in OKLab so they look even.
 */

export interface Palette {
  id: string;
  name: string;
  stops: string[];
  /** True when the gradient wraps smoothly (end colour = start colour). */
  cyclic?: boolean;
}

export const PALETTES: readonly Palette[] = [
  {
    id: 'neon-night',
    name: 'Neon Night',
    stops: ['#0b0221', '#3a0ca3', '#7209b7', '#f72585', '#ffd166'],
  },
  { id: 'aurora', name: 'Aurora', stops: ['#001219', '#005f73', '#0a9396', '#94d2bd', '#e9d8a6'] },
  { id: 'sunset', name: 'Sunset', stops: ['#1a0b2e', '#6a0572', '#e63946', '#f4a261', '#ffe8a3'] },
  {
    id: 'ocean',
    name: 'Deep Ocean',
    stops: ['#03045e', '#0077b6', '#00b4d8', '#90e0ef', '#caf0f8'],
  },
  {
    id: 'fire',
    name: 'Fire',
    stops: ['#000000', '#3d0000', '#9d0208', '#e85d04', '#faa307', '#ffffb7'],
  },
  { id: 'acid', name: 'Acid', stops: ['#0d0d0d', '#1b4332', '#2dc653', '#b5ff00', '#f0ff8a'] },
  { id: 'ice', name: 'Ice', stops: ['#0b132b', '#1c2541', '#3a506b', '#5bc0be', '#e0fbfc'] },
  {
    id: 'vapor',
    name: 'Vaporwave',
    stops: ['#120458', '#7a04eb', '#ff00a0', '#fe75fe', '#00f0ff'],
  },
  { id: 'forest', name: 'Forest', stops: ['#081c15', '#1b4332', '#40916c', '#95d5b2', '#d8f3dc'] },
  { id: 'gold', name: 'Gold', stops: ['#0a0a0a', '#3d2c00', '#b8860b', '#ffd700', '#fff4c2'] },
  { id: 'mono', name: 'Monochrome', stops: ['#000000', '#ffffff'] },
  {
    id: 'synthwave',
    name: 'Synthwave',
    stops: ['#0d0221', '#261447', '#ff3864', '#ff9e00', '#fdf500'],
  },
  {
    id: 'cyberpunk',
    name: 'Cyberpunk',
    stops: ['#000000', '#0abdc6', '#ea00d9', '#711c91', '#f6f740'],
  },
  { id: 'lava', name: 'Lava', stops: ['#1b0000', '#5c0000', '#c1121f', '#ff6b00', '#ffd23f'] },
  {
    id: 'deep-sea',
    name: 'Deep Sea',
    stops: ['#00010d', '#001b44', '#00467f', '#00a6a6', '#b6fff4'],
  },
  { id: 'rose', name: 'Rose Gold', stops: ['#1a0e10', '#5a2a33', '#b76e79', '#e8b4b8', '#fff1ed'] },
  {
    id: 'emerald',
    name: 'Emerald City',
    stops: ['#001a0f', '#004d2b', '#00a36c', '#7cffb2', '#f2fff6'],
  },
  {
    id: 'ultraviolet',
    name: 'Ultraviolet',
    stops: ['#05000f', '#2e0854', '#5f0f99', '#a64dff', '#e0c2ff'],
  },
  { id: 'citrus', name: 'Citrus', stops: ['#0f1400', '#3d5a00', '#9acd32', '#ffd60a', '#fff7d6'] },
  {
    id: 'candy',
    name: 'Candy',
    stops: ['#ff9ff3', '#feca57', '#48dbfb', '#ff6b6b', '#1dd1a1', '#ff9ff3'],
    cyclic: true,
  },
  { id: 'sakura', name: 'Sakura', stops: ['#1b0f1a', '#5e2750', '#d6609a', '#ffb7d5', '#fff5f9'] },
  {
    id: 'arctic',
    name: 'Arctic Night',
    stops: ['#020812', '#0b2545', '#13315c', '#8da9c4', '#eef4ed'],
  },
  {
    id: 'toxic',
    name: 'Toxic',
    stops: ['#000000', '#1a2f00', '#39ff14', '#ccff00', '#000000'],
    cyclic: true,
  },
  {
    id: 'blood-moon',
    name: 'Blood Moon',
    stops: ['#000000', '#2b0000', '#8a0303', '#ff4d00', '#ffb38a'],
  },
  {
    id: 'sunrise',
    name: 'Sunrise',
    stops: ['#1d1135', '#5c2a9d', '#ff6f91', '#ffc75f', '#f9f871'],
  },
  {
    id: 'teal-orange',
    name: 'Teal & Orange',
    stops: ['#001219', '#0a9396', '#e9d8a6', '#ee9b00', '#bb3e03'],
  },
  { id: 'galaxy', name: 'Galaxy', stops: ['#03001c', '#301e67', '#5b8fb9', '#b6eada', '#ffffff'] },
  { id: 'ember', name: 'Ember', stops: ['#0b0500', '#3a1500', '#8f2d00', '#ff7b00', '#ffe8b0'] },
  {
    id: 'holo',
    name: 'Holographic',
    stops: ['#a1c4fd', '#c2e9fb', '#fbc2eb', '#a18cd1', '#84fab0', '#a1c4fd'],
    cyclic: true,
  },
  {
    id: 'spectrum',
    name: 'Spectrum',
    stops: ['#ff0040', '#ff8000', '#ffe600', '#00e676', '#00b0ff', '#7c4dff', '#ff0040'],
    cyclic: true,
  },
];

export const PALETTE_SIZE = 256;

export function findPalette(id: string): Palette {
  return PALETTES.find((p) => p.id === id) ?? PALETTES[0]!;
}

/** A scene's palette: inline custom stops win over the built-in id. */
export function paletteFromRef(ref: {
  id: string;
  stops?: string[];
  cyclic?: boolean;
  name?: string;
}): Palette {
  if (ref.stops && ref.stops.length >= 2) {
    return {
      id: ref.id,
      name: ref.name ?? 'Custom',
      stops: ref.stops,
      ...(ref.cyclic ? { cyclic: true } : {}),
    };
  }
  return findPalette(ref.id);
}

// ---- colour math (sRGB ↔ linear ↔ OKLab)

type Vec3 = [number, number, number];

export function hexToRgb(hex: string): Vec3 {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  const n = parseInt(full, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
const toSrgb = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);

export function srgbToOklab([r, g, b]: Vec3): Vec3 {
  const lr = toLinear(r);
  const lg = toLinear(g);
  const lb = toLinear(b);
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

export function oklabToSrgb([L, a, b]: Vec3): Vec3 {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const clamp = (v: number) => Math.min(1, Math.max(0, v));
  return [
    clamp(toSrgb(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s)),
    clamp(toSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s)),
    clamp(toSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s)),
  ];
}

/** Bake a palette into RGBA8 texels. */
export function bakePalette(p: Palette, size = PALETTE_SIZE): Uint8Array {
  const labs = p.stops.map((s) => srgbToOklab(hexToRgb(s)));
  const out = new Uint8Array(size * 4);
  const segments = labs.length - 1;
  for (let i = 0; i < size; i++) {
    const t = segments === 0 ? 0 : (i / (size - 1)) * segments;
    const k = Math.min(segments - 1, Math.floor(t));
    const f = segments === 0 ? 0 : t - k;
    const a = labs[Math.max(0, k)]!;
    const b = labs[Math.min(labs.length - 1, k + 1)]!;
    const rgb = oklabToSrgb([
      a[0] + (b[0] - a[0]) * f,
      a[1] + (b[1] - a[1]) * f,
      a[2] + (b[2] - a[2]) * f,
    ]);
    out[i * 4] = Math.round(rgb[0] * 255);
    out[i * 4 + 1] = Math.round(rgb[1] * 255);
    out[i * 4 + 2] = Math.round(rgb[2] * 255);
    out[i * 4 + 3] = 255;
  }
  return out;
}

/** CSS gradient for UI swatches. */
export function paletteCss(p: Palette): string {
  return `linear-gradient(90deg, ${p.stops.join(', ')})`;
}
